import React, { useEffect, useState } from 'react';
import { FieldProps } from 'formik';
import DateFnsUtils from '@date-io/luxon';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  DatePicker,
  KeyboardDatePickerProps,
  DatePickerView,
} from '@material-ui/pickers';
import { useTranslation } from 'react-i18next';
import get from 'lodash.get';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { appTheme } from 'components/AppTheme';
import { DateTime } from 'luxon';
import { getErrorMessageKey } from 'helpers/helpers';
import { withField } from 'components/FormFields/withFields';
import createMuiTheme, { Theme } from '@material-ui/core/styles/createTheme';
import { useValueStyles } from './styles';

const formDatePickerTheme: Theme = createMuiTheme(appTheme, {
  overrides: {
    MuiPickersToolbarButton: {
      toolbarBtn: {
        padding: {
          top: '.5em',
          bottom: '.5em',
          left: '2em',
          right: '2em',
        },
        marginBottom: appTheme.spacing(1),
        '&:after': {
          content: '"▼"',
          display: 'inline-block',
          opacity: '.4',
          marginLeft: '2px',
        },
      },
    },
    MuiPickersDay: {
      '& p': {
        marginBottom: '0 !important',
      },
    },
  },
});

interface OwnProps {
  id?: string;
  label?: string;
  name: string;
  format?: string;
  onChangeSubmit?: boolean;
  specificError?: boolean;
  openTo?: DatePickerView;
  views?: DatePickerView[];
  onChange?: (date: DateTime) => any;
  time?: boolean;
}

type Props = OwnProps & Partial<KeyboardDatePickerProps> & FieldProps;

/**
 * Basic wrapper around DateTimePicker
 * @param props
 * @constructor
 */
const FormDatePicker: React.FC<Props> = (props) => {
  const {
    label,
    field,
    id,
    format,
    inputVariant,
    onChangeSubmit,
    form,
    disableToolbar,
    disabled,
    readOnly,
    meta,
    openTo = 'year',
    views = ['year', 'date'],
    onChange,
    time,
    ...rest
  } = props;
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { name, value } = field;
  const valueClasses = useValueStyles();
  // managing with own state, so backspace work normally
  const [date, setDate] = useState<null | DateTime>(null);
  const variant = props.variant || 'inline';
  const isDialog = variant === 'dialog';
  const dateFormat = format || 'dd.MM.yyyy';
  const addedProps = isDialog
    ? {
        cancelLabel: t('general_cancel'),
        clearLabel: t('general_delete'),
        okLabel: t('picker_okLabel'),
        todayLabel: t('picker_todayLabel'),
      }
    : {};

  useEffect(() => {
    if (date?.toSQLDate() !== value) {
      setDate(value ? DateTime.fromSQL(value) : null);
    }
  }, [value]); // eslint-disable-line

  // decide min, max, missing or invalid
  const errorMessage = (() => {
    if (meta.error && date) {
      // when there is something in the local state, the error wont be "field is missing"
      // but field is invalid
      const errorType = meta.error.split('.').slice(-1)[0];
      return errorType === 'missing'
        ? t(getErrorMessageKey('invalid'))
        : // if there is date, and it's not missing, it's max or min
          // use specificError for the date
          t(getErrorMessageKey(meta.error, true));
    }

    if (meta.error) {
      return t(getErrorMessageKey('missing'));
    }
    return '';
  })();

  const DatepickerComponent = readOnly ? DatePicker : KeyboardDatePicker;
  return (
    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={language}>
      <MuiThemeProvider theme={formDatePickerTheme}>
        <DatepickerComponent
          openTo={openTo}
          views={views}
          fullWidth
          variant={variant}
          inputVariant={inputVariant || 'outlined'}
          format={dateFormat}
          invalidLabel={t('picker_invalidLabel')}
          maxDateMessage={t('picker_overMaxMessage')}
          minDateMessage={t('picker_underMinMessage')}
          disableToolbar={disableToolbar}
          placeholder={DateTime.local().toFormat(dateFormat)}
          id={id || name}
          label={label}
          disabled={disabled}
          readOnly={readOnly}
          className={readOnly ? valueClasses.readOnly : ''}
          {...field}
          {...addedProps}
          value={!value ? date : value}
          onChange={(date: DateTime | null) => {
            setDate(date);
            form.setFieldValue(
              name,
              date
                ? time
                  ? `${date.toSQLDate()}T${date.toSQLTime() ? date.toSQLTime().replace(' ', '') : ''}`
                  : date.toSQLDate()
                : '',
            );
            typeof onChange === 'function' && onChange(date);
          }}
          onClose={() => {
            if (!onChangeSubmit || !field || !form) return;
            const initial = field ? get(form, `initialValues.${name}`) : null;
            if (value !== initial) setTimeout(form.submitForm, 100);
          }}
          helperText={errorMessage}
          error={!!errorMessage}
          {...rest}
        />
      </MuiThemeProvider>
    </MuiPickersUtilsProvider>
  );
};

export default withField(FormDatePicker);
